import React from 'react';
import { PieChart, Pie, Tooltip } from 'recharts';
import {
  FiMail,
  FiMessageSquare,
  FiFileText,
  FiAlertTriangle,
  FiRefreshCw
} from 'react-icons/fi';

import type {
  ActivityStatsData,
  WorkflowTypeStatsData
} from '../../../services/analyticsService';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ChartEntry {
  name: string;
  value: number;
  fill: string;
}

// ── Static config ─────────────────────────────────────────────────────────────

const CHART_SEGMENTS: Array<{
  key: 'insured' | 'prospect' | 'policy';
  name: string;
  fill: string;
}> = [
  { key: 'insured', name: 'Insured', fill: '#91d5ff' },
  { key: 'prospect', name: 'Prospect', fill: '#76838f' },
  { key: 'policy', name: 'Policy', fill: '#33a4e0' }
];

const ACTIVITY_META: Array<{
  key: keyof ActivityStatsData;
  label: string;
  icon: React.ReactNode;
  bg: string;
  color: string;
}> = [
  {
    key: 'emails_sent',
    label: 'Emails Sent',
    icon: <FiMail size={13} />,
    bg: '#34875a22',
    color: '#34875a'
  },
  {
    key: 'sms_sent',
    label: 'SMS Sent',
    icon: <FiMessageSquare size={13} />,
    bg: '#a0522d22',
    color: '#a0522d'
  },
  {
    key: 'ams_tasks',
    label: 'AMS Tasks',
    icon: <FiFileText size={13} />,
    bg: '#006de022',
    color: '#006de0'
  }
];

// ── Custom tooltip ────────────────────────────────────────────────────────────

const CustomTooltip: React.FC<{
  active?: boolean;
  payload?: Array<{ name: string; value: number; payload: ChartEntry }>;
}> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div className='mws-wst-tooltip'>
      <span
        className='mws-wst-tooltip__dot'
        style={{ background: d.payload.fill }}
      />
      <span className='mws-wst-tooltip__name'>{d.name}</span>
      <strong className='mws-wst-tooltip__count'>{d.value}</strong>
    </div>
  );
};

// ── Widget ────────────────────────────────────────────────────────────────────

interface Props {
  workflowTypeStats: WorkflowTypeStatsData | null;
  activityStats: ActivityStatsData | null;
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
}

const WorkflowStatsWidget: React.FC<Props> = ({
  workflowTypeStats,
  activityStats,
  isLoading,
  error,
  onRetry
}) => {
  const chartData: ChartEntry[] = CHART_SEGMENTS.map(seg => ({
    name: seg.name,
    fill: seg.fill,
    value:
      seg.key === 'insured'
        ? (workflowTypeStats?.insured_workflows.total ?? 0)
        : seg.key === 'prospect'
          ? (workflowTypeStats?.prospect_workflows.total ?? 0)
          : (workflowTypeStats?.policy_workflows.total ?? 0)
  }));

  const chartTotal = chartData.reduce((s, d) => s + d.value, 0);

  const errorBlock = (
    <div className='mws-error'>
      <span className='mws-error__msg'>
        <FiAlertTriangle size={12} /> {error}
      </span>
      <button onClick={onRetry} className='mws-error__retry'>
        <FiRefreshCw size={10} /> Retry
      </button>
    </div>
  );

  return (
    <>
      {/* ── Workflow Breakdown ───────────────────────────────────────────────── */}
      <div className='dw-card' style={{ padding: '12px 14px' }}>
        <div className='dw-card__label' style={{ marginBottom: 12 }}>
          Workflow Breakdown
        </div>
        {isLoading ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12
            }}
          >
            <div
              className='dw-skel mws-wst-chart-skel'
              style={{ width: 130, height: 130 }}
            />
            <div
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 8
              }}
            >
              {[1, 2, 3].map(i => (
                <div
                  key={i}
                  className='dw-skel'
                  style={{ height: 12, borderRadius: 4 }}
                />
              ))}
            </div>
          </div>
        ) : error ? (
          errorBlock
        ) : chartTotal === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div className='mws-wst-chart-empty'>—</div>
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12
            }}
          >
            <PieChart width={130} height={130}>
              <Pie
                data={chartData}
                cx={65}
                cy={65}
                innerRadius={36}
                outerRadius={58}
                dataKey='value'
                strokeWidth={0}
                paddingAngle={2}
                startAngle={90}
                endAngle={-270}
              />
              <Tooltip
                content={(props: any) => <CustomTooltip {...props} />}
                wrapperStyle={{ zIndex: 9999 }}
              />
            </PieChart>
            <div className='mws-wst-chart-legend' style={{ width: '100%' }}>
              {chartData.map(d => (
                <div key={d.name} className='mws-wst-chart-legend-row'>
                  <span
                    className='mws-wst-dot'
                    style={{ background: d.fill }}
                  />
                  <span className='mws-wst-chart-legend-name'>{d.name}</span>
                  <span className='mws-wst-chart-legend-val'>{d.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Activity Stats ───────────────────────────────────────────────────── */}
      <div className='dw-card' style={{ padding: '12px 14px' }}>
        <div className='dw-card__label' style={{ marginBottom: 10 }}>
          Activity Stats
        </div>
        {isLoading ? (
          <div className='mws-wst-stats'>
            {[1, 2, 3].map(i => (
              <div
                key={i}
                className='dw-skel'
                style={{ height: 70, borderRadius: 8 }}
              />
            ))}
          </div>
        ) : error ? (
          errorBlock
        ) : (
          <div className='mws-wst-stats'>
            {ACTIVITY_META.map(({ key, label, icon, bg, color }) => (
              <div key={key} className='mws-wst-stat'>
                <div
                  className='mws-wst-stat__icon'
                  style={{ background: bg, color }}
                >
                  {icon}
                </div>
                <div className='mws-wst-stat__val'>
                  {(activityStats?.[key] ?? 0).toLocaleString('en-US')}
                </div>
                <div className='mws-wst-stat__lbl'>{label}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default WorkflowStatsWidget;
